export type SavedMeteorSize = 'small' | 'medium' | 'large';

export type SavedMoonCrater = {
  id: number;
  size: SavedMeteorSize;
  normal: { x: number; y: number; z: number };
  radius: number;
  depth: number;
  hasMare: boolean;
  mareFillScale: number;
};

export type MoonDesignDraft = {
  surfaceSeed: number;
  craterCount: number;
  mareCount: number;
  craters: SavedMoonCrater[];
};

export type SavedMoonDesign = MoonDesignDraft & {
  version: 1;
  id: string;
  createdAt: number;
  name: string;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isSavedCrater = (value: unknown): value is SavedMoonCrater => {
  if (!value || typeof value !== 'object') return false;
  const crater = value as Partial<SavedMoonCrater>;
  return Number.isInteger(crater.id)
    && (crater.size === 'small' || crater.size === 'medium' || crater.size === 'large')
    && Boolean(crater.normal)
    && isFiniteNumber(crater.normal?.x)
    && isFiniteNumber(crater.normal?.y)
    && isFiniteNumber(crater.normal?.z)
    && isFiniteNumber(crater.radius)
    && isFiniteNumber(crater.depth)
    && typeof crater.hasMare === 'boolean'
    && isFiniteNumber(crater.mareFillScale);
};

export function isSavedMoonDesign(value: unknown): value is SavedMoonDesign {
  if (!value || typeof value !== 'object') return false;
  const design = value as Partial<SavedMoonDesign>;
  return design.version === 1
    && typeof design.id === 'string'
    && design.id.length > 0
    && typeof design.name === 'string'
    && isFiniteNumber(design.createdAt)
    && isFiniteNumber(design.surfaceSeed)
    && Number.isInteger(design.craterCount)
    && Number.isInteger(design.mareCount)
    && Array.isArray(design.craters)
    && design.craters.length <= 40
    && design.craters.every(isSavedCrater);
}

export function createMoonId(now = Date.now()): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `moon-${now}-${Math.random().toString(36).slice(2, 9)}`;
}
